import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import { Router } from '@angular/router';

import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';

import { ConstantsService } from 'app/services/constants-service';
import { GlobalValuesService } from 'app/services/global-values.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';

import { CreateOrganisationComponent } from '../createorganisation/create-organisation.component';
import { CreatePersonComponent } from '../createperson/create-person.component';

import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';

import { GenericSearchService } from './generic-search.service';
import { Observable } from 'rxjs';
import { InstantScreeningDialogComponent } from '../instant-screening-dialog/instant-screening-dialog.component';

@Component({
  selector: 'app-header-search',
  providers: [GenericSearchService],
  templateUrl: './generic-search.component.html'
})

/**
 * Component for Header Search Results and Pop-up items.
 */
export class GenericSearchComponent {
  private static ORGANISATION = 'Org';
  private static PERSON = 'Person';
  private static INSTANT_SCREENING = 'InstantScreening';

  // Overall filtered search results
  filteredSearchResults: any[] = [];
  // Model value for search box.
  @Input() searchValue: string;
  // Search Query string.
  query: string;

  config: MdDialogConfig = {
    disableClose: true,
    width: '',
    height: '',
    position: {
      top: '',
      bottom: '',
      left: '',
      right: ''
    }
  };

  @Input() searchURI: string;
  @Input() isRedirect: boolean = true;
  @Input() searchPlaceholder: string;
  @Output() change: EventEmitter<string> = new EventEmitter();
  @Output() clientId: EventEmitter<number> = new EventEmitter();
  @Output() searchDetails: EventEmitter<GenericSearchDetails> = new EventEmitter();

  // Constructor method
  constructor(private genericSearchService: GenericSearchService,
    public dialog: MdDialog,
    private globalValuesService: GlobalValuesService,
    private router: Router,
    private trackingService: TrackingService,
    private snackBarService: SnackBarService) {
  }

  onChange() {
    this.change.emit(this.searchValue);
  }

  // Method to filter search results and display them in search box.
  filterSearchResults(event) {
    // Search Query
    this.query = event.query;
    this.genericSearchService.getHeaderSearchResults(this.query, this.searchURI)
      .subscribe(response => {
        if (this.searchPlaceholder === MessageService.HEADER_SEARCH_PLACEHOLDER
          || this.searchPlaceholder === MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER
          || this.searchPlaceholder === MessageService.COUNTER_PARTY_SEARCH_PLACEHOLDER
          || this.searchPlaceholder === MessageService.ARCHIVAL_ENTITY_SEARCH_PLACEHOLDER
          || this.searchPlaceholder === MessageService.ORGANISATION_SEARCH_PLACEHOLDER) {
          this.handleHeaderSearchResults(response);
        } else if (this.searchPlaceholder === MessageService.CLIENT_SEARCH_PLACEHOLDER) {
          this.handleClientSuggestionsReponse(response);
        }
        this.trackingService
          .emitEvent(TrackingConstants.GENERIC_SEARCH, TrackingConstants.GENERIC_SEARCH, TrackingConstants.SUCCESS, 1);
      },
      err => {
        this.callSnackBarService(err, '', MessageService.GENERIC_SEARCH_FAILURE);
        this.trackingService
          .emitEvent(TrackingConstants.GENERIC_SEARCH, TrackingConstants.GENERIC_SEARCH, TrackingConstants.FAILURE, 0);
      }
      );
  }

  callSnackBarService(response: any, successMsg: string, failureMsg: string) {
    this.snackBarService.showStatus(response, successMsg, failureMsg);
  }

  resetHeaderSearchValues(selectedSearchResult) {
    // By default selected search value is filled in search box on selection
    // But in case of clicking 'New Person' or 'New Organisation' link, we want
    // to keep the search box blank (Only with placeholder). So, we are making
    // that change in following condition.
    const genericSearchDetails = new GenericSearchDetails();
    if (selectedSearchResult.type === GenericSearchComponent.INSTANT_SCREENING) {
      this.openInstantScreeningDialog();
      this.searchValue = '';
      return;
    }
    if (selectedSearchResult.type === 'NewOrg') {
      this.openNewOrgDialog().subscribe(result => {
        genericSearchDetails.entityType = 'org';
        genericSearchDetails.clientId = result.clientId;
        genericSearchDetails.id = result.id;
        genericSearchDetails.clientName = result.name;
        genericSearchDetails.entityId = result.entityId;
        //TODO: Need to handle in a better way.
        if (this.searchPlaceholder === MessageService.ORGANISATION_SEARCH_PLACEHOLDER) {
          this.searchDetails.emit(genericSearchDetails);
        } else {
          this.emitAndRedirect(genericSearchDetails);
        }
      });
    } else if (selectedSearchResult.type === 'NewPerson') {
      this.openNewPersonDialog().subscribe(result => {
        genericSearchDetails.entityType = 'person';
        genericSearchDetails.id = result.id;
        genericSearchDetails.clientName = this.parsePersonName(result);
        genericSearchDetails.entityId = result.entityId;
        this.emitAndRedirect(genericSearchDetails);
      });
    } else if (this.searchPlaceholder === MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER
      || this.searchPlaceholder === MessageService.COUNTER_PARTY_SEARCH_PLACEHOLDER
      || this.searchPlaceholder === MessageService.CLIENT_SEARCH_PLACEHOLDER
      || this.searchPlaceholder === MessageService.ARCHIVAL_ENTITY_SEARCH_PLACEHOLDER) {
      genericSearchDetails.clientId = selectedSearchResult.clientId;
      genericSearchDetails.entityId = selectedSearchResult.entityId;
      genericSearchDetails.clientName = selectedSearchResult.value;
      // Following values are required for archival screen.
      genericSearchDetails.clientType = selectedSearchResult.type;
      genericSearchDetails.clientGender = selectedSearchResult.genderLegal;
      genericSearchDetails.clientLogo = selectedSearchResult.profileLogo;
      genericSearchDetails.sanctionStatus = selectedSearchResult.sanctionStatus;
      genericSearchDetails.pepStatus = selectedSearchResult.pepStatus;
      genericSearchDetails.badPressStatus = selectedSearchResult.badPressStatus;
      this.searchDetails.emit(genericSearchDetails);
      //TODO: Need to handle in a better way.
    } else if (this.searchPlaceholder === MessageService.ORGANISATION_SEARCH_PLACEHOLDER) {
      genericSearchDetails.clientName = selectedSearchResult.value;
      genericSearchDetails.id = selectedSearchResult.id;
      this.searchDetails.emit(genericSearchDetails);
    }
    else if (this.searchPlaceholder === MessageService.HEADER_SEARCH_PLACEHOLDER) {
      // Get the entity's values to be passed to be URL that will update
      // the Iframe.
      const entityUrl = selectedSearchResult.linkUri;
      genericSearchDetails.entityType = selectedSearchResult.type;
      genericSearchDetails.entityId = selectedSearchResult.entityId;
      genericSearchDetails.clientId = selectedSearchResult.clientId;
      genericSearchDetails.id = selectedSearchResult.id;
      genericSearchDetails.clientName = selectedSearchResult.value;
      genericSearchDetails.sanctionStatus = selectedSearchResult.sanctionStatus;
      genericSearchDetails.pepStatus = selectedSearchResult.pepStatus;
      genericSearchDetails.badPressStatus = selectedSearchResult.badPressStatus;
      this.emitAndRedirect(genericSearchDetails);
    }
    // Always clear search value after selection
    this.searchValue = '';
  }

  handleHeaderSearchResults(response) {
    // Reset final search results array to blank value to refresh
    // search data for each search iteration.
    this.filteredSearchResults = [];
    // This object is a set of two inner objects.
    // persons: Object of persons search results
    // organisations: Object of organisations search results
    const arrHeaderSearchResults = response.arrResult;
    const personResults = this.parsePersonResponse(arrHeaderSearchResults.persons);
    const orgResults = this.parseOrganisationSearchResponse(arrHeaderSearchResults.organisations);
    if (personResults.length > 0 && this.searchPlaceholder !== MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER && this.searchPlaceholder !== MessageService.ORGANISATION_SEARCH_PLACEHOLDER) {
      this.filteredSearchResults = this.filteredSearchResults.concat(personResults);
    }

    if (orgResults.length > 0) {
      this.filteredSearchResults = this.filteredSearchResults.concat(orgResults);
    }

    // For entity search, we don't need New Person and New Org links.
    if (this.searchPlaceholder !== MessageService.ARCHIVAL_ENTITY_SEARCH_PLACEHOLDER &&
      this.searchPlaceholder !== MessageService.COUNTER_PARTY_SEARCH_PLACEHOLDER &&
      this.searchPlaceholder !== MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER) {
      /* Add Person and Add Organisation Links */
      // Add New Organisation link to search result unconditionally.
      this.filteredSearchResults.push(
        {
          'value': 'New Organisation',
          'type': 'NewOrg',
          'dob': null,
          'genderLegal': null,
          'countryReg': null,
          'display': null,
          'id': 0,
          'linkUri': null,
          'profileLogo': null,
          'entityId': 0
        });

      if (this.searchPlaceholder !== MessageService.ORGANISATION_SEARCH_PLACEHOLDER) {
        this.filteredSearchResults.push(
          {
            'value': 'New Person',
            'type': 'NewPerson',
            'dob': null,
            'genderLegal': null,
            'countryReg': null,
            'display': null,
            'id': 0,
            'linkUri': null,
            'profileLogo': null,
            'entityId': 0
          });
      }
      this.filteredSearchResults.push(
        {
          'value': 'Instant Screening',
          'type': GenericSearchComponent.INSTANT_SCREENING,
          'dob': null,
          'genderLegal': null,
          'countryReg': null,
          'display': null,
          'id': 0,
          'linkUri': null,
          'profileLogo': null,
          'entityId': 0
        });
    }
  }

  handleClientSuggestionsReponse(response) {
    // Reset final search results array to blank value to refresh
    // search data for each search iteration.
    this.filteredSearchResults = [];
    this.filteredSearchResults =
      this.filteredSearchResults.concat(this.parseOrganisationSearchResponse(response.arrResult.organisations));
  }

  parsePersonResponse(personResults) {
    let profileLogo = '';
    let personName = '';
    // Get count of Persons search result.
    const countPersonSearchResults: number = personResults.length;
    // Initialize Persons formatted search results array to blank array.
    const arrFormatPersonSearchResults: any[] = [];
    // Check for persons array and format it to display in search box.
    if (personResults && countPersonSearchResults > 0) {
      for (let resultCounter = 0; resultCounter < countPersonSearchResults; resultCounter++) {
        // Set Object for record specific data.
        const searchRecordData = personResults[resultCounter];
        if (searchRecordData.logo) {
          profileLogo = searchRecordData.logo;
        } else {
          // If male person, display male icon otherwise display female icon.
          profileLogo = (searchRecordData.genderName && searchRecordData.genderName.toLowerCase() === 'male' ?
            ConstantsService.DEFAULT_PERSON_MALE_LOGO : ConstantsService.DEFAULT_PERSON_FEMALE_LOGO);
        }
        // Set Person Full Name
        personName = this.parsePersonName(searchRecordData);
        // Add person record to array of results.
        arrFormatPersonSearchResults.push(
          {
            'value': personName,
            'type': GenericSearchComponent.PERSON,
            'dob': searchRecordData.birthday,
            'genderLegal': searchRecordData.genderName,
            'countryReg': searchRecordData.nationalityName,
            'display': null,
            'id': searchRecordData.id,
            'linkUri': ConstantsService.PERSON_DETAILS_PAGE,
            'profileLogo': profileLogo,
            'entityId': searchRecordData.entityId,
            'sanctionStatus': searchRecordData.sanctionStatus,
            'pepStatus': searchRecordData.pepStatus,
            'badPressStatus': searchRecordData.badPressStatus
          });
      }
    }
    return arrFormatPersonSearchResults;
  }

  private parsePersonName(searchRecordData: any) {
    let personName = '';
    if (searchRecordData.wholeName && '' !== searchRecordData.wholeName.trim()) {
      personName = searchRecordData.wholeName.trim();
    } else {
      personName = searchRecordData.firstNames;
      if (searchRecordData.middleNames && '' !== searchRecordData.middleNames.trim()) {
        personName = personName + ' ' + searchRecordData.middleNames;
      }
      personName = personName + ' ' + searchRecordData.lastNames;
      personName = personName.trim();
    }
    return personName;
  }

  parseOrganisationSearchResponse(organisationResponse) {
    let profileLogo = '';
    let orgName = '';
    // Get count of Organisations search result.
    const countOrgSearchResults: number = organisationResponse.length;
    // Initialize Organisations formatted search results array to blank array.
    const arrFormatOrgSearchResults: any[] = [];
    // Check for Organisations array and format it to display in search box.
    if (organisationResponse && countOrgSearchResults > 0) {
      for (let resultCounter = 0; resultCounter < countOrgSearchResults; resultCounter++) {
        // Set Object for record specific data.
        const searchRecordData = organisationResponse[resultCounter];
        profileLogo = (searchRecordData.logo ? searchRecordData.logo :
          ConstantsService.DEFAULT_ORG_LOGO);
        // Set the Organization name if present.
        if (searchRecordData.name && '' !== searchRecordData.name.trim()) {
          orgName = searchRecordData.name.trim();
        }
        // Add Organization record to array of results.
        arrFormatOrgSearchResults.push(
          {
            'value': orgName.toLowerCase(),
            'type': GenericSearchComponent.ORGANISATION,
            'dob': searchRecordData.incorporatedDate,
            'genderLegal': searchRecordData.legalName,
            'countryReg': searchRecordData.registrationCode,
            'display': null,
            'id': searchRecordData.id,
            'linkUri': ConstantsService.ORG_DETAILS_PAGE,
            'profileLogo': profileLogo,
            'clientId': searchRecordData.clientId,
            'entityId': searchRecordData.entityId,
            'sanctionStatus': searchRecordData.sanctionStatus,
            'pepStatus': searchRecordData.pepStatus,
            'badPressStatus': searchRecordData.badPressStatus,
          });
      }
    }
    return arrFormatOrgSearchResults;
  }

  private openNewOrgDialog(): Observable<any> {
    this.trackingService
      .emitEvent(TrackingConstants.GENERIC_SEARCH, TrackingConstants.OPEN_NEW_ORG_LINK, TrackingConstants.SUCCESS, 1);
    let dialogRef: MdDialogRef<CreateOrganisationComponent>;
    dialogRef = this.dialog.open(CreateOrganisationComponent, this.config);
    dialogRef.componentInstance.redirectAfterCreate = this.isRedirect;
    dialogRef.componentInstance.orgName = this.query;
    return dialogRef.afterClosed();
  }

  private openNewPersonDialog(): Observable<any> {
    this.trackingService
      .emitEvent(TrackingConstants.GENERIC_SEARCH, TrackingConstants.OPEN_NEW_PERSON_LINK, TrackingConstants.SUCCESS, 1);
    let dialogRef: MdDialogRef<CreatePersonComponent>;
    dialogRef = this.dialog.open(CreatePersonComponent, this.config);
    dialogRef.componentInstance.redirectAfterCreate = this.isRedirect;
    dialogRef.componentInstance.firstName = this.query;
    return dialogRef.afterClosed();
  }

  private openInstantScreeningDialog(): void {
    let dialogRef: MdDialogRef<InstantScreeningDialogComponent>;
    dialogRef = this.dialog.open(InstantScreeningDialogComponent, this.config);
    dialogRef.componentInstance.screenName = (this.query || '').trim();
  }

  private emitAndRedirect(searchDetails: GenericSearchDetails) {
    this.searchDetails.emit(searchDetails);
    if (!this.isRedirect) return;
    this.router.navigate(['/entity', searchDetails.entityId, 'details']);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/genericsearch/generic-search.component.ts